import { Link, useLocation } from "react-router";
import ButtonFactory from "@components/Button/ButtonFactory";
import { ButtonType } from "@components/Button/utils/types";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import { formatAudioDuration } from "@routes/Article/utils/helpers";
import { FeedArticleItems } from "@routes/Article/utils/types";
import { Track, useTrackPlayback } from "@sphtech/web2-sticky-audio-player";
import { cn } from "@util/helpers";

type PodcastComingUpNextCardProps = {
  /** Podcast article to display on slider */
  podcastArticle: FeedArticleItems;
  /** Index of the podcast article used to add styling on cards */
  index: number;
  /** Flag to check if card is coming from podcast about section to add conditional ga tracking details */
  isFromAbout: boolean;
};

/**
 * Component for Podcast Coming up next Section Card.
 * https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=4844-133279&m=dev
 */
const PodcastComingUpNextCard = ({
  podcastArticle,
  index,
  isFromAbout,
}: PodcastComingUpNextCardProps): React.ReactElement => {
  const sendDataToGTM = useSendDataToGTM();
  const { pathname } = useLocation();
  const { title, podcast, publishTime, shortUrl, urlPath } = podcastArticle;
  const duration = podcast?.duration || 0;
  const audioUrl = podcast?.url;
  const track: Track = {
    audioTitle: title,
    audioUrl: audioUrl,
    audioBannerImage: podcast?.tag.imageUrl,
    audioPublishedDate: publishTime,
    audioShareUrl: shortUrl || urlPath,
  };

  const { handleTrack, isPlayingCurrentAudio } = useTrackPlayback({
    title: title,
    trackData: track,
  });

  const aboutEventAction = `click::carousel::podcast series::${index + 1}::${title}`;
  const eventAction = `click::carousel::coming up next::${index + 1}`;
  const trackingObject = {
    event: "custom_event",
    eventCategory: "podcast",
    eventAction: isFromAbout ? aboutEventAction : eventAction,
    eventLabel: title,
  };

  const handleOnClickSendData = () => {
    if (typeof window === "undefined") return;
    if (!trackingObject) return;

    if (trackingObject?.eventLabel === undefined) {
      trackingObject.eventLabel = pathname;
    }

    sendDataToGTM(trackingObject);
  };

  const handleButtonClick = () => {
    handleTrack();
    handleOnClickSendData();
  };

  return (
    <div
      data-testid="podcast-coming-up-next-component"
      className={cn(
        "flex h-full flex-col justify-between border-l border-gray-175 px-4",
        { "border-l-0 pl-0": index === 0, "lg:border-l-0 lg:pl-0": index === 4 }
      )}
    >
      <div className="flex flex-row items-start gap-1">
        <div className="mt-1">
          <IconFactory type={IconTypeEnum.headphone} x={9} y={9} size={15} />
        </div>

        <Link
          to={urlPath}
          onClick={handleOnClickSendData}
          className="font-base font-lct font-bold"
          reloadDocument
        >
          {title}
        </Link>
      </div>

      <ButtonFactory
        buttonType={ButtonType.Secondary}
        iconPosition="left"
        className="mt-2 flex w-fit flex-row items-center px-3 py-2 font-poppins text-2xs font-medium"
        onClick={handleButtonClick}
        icon={
          <IconFactory
            type={
              isPlayingCurrentAudio ? IconTypeEnum.pause : IconTypeEnum.play
            }
            size={15}
            x={38}
            y={30}
            className="ml-2 mr-1 stroke-black stroke-2 text-white"
          />
        }
        text={formatAudioDuration(duration)}
      />
    </div>
  );
};

export default PodcastComingUpNextCard;
